import { useEffect, useRef } from 'react'
import * as THREE from 'three'

export const ParticleWaveBackground: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<{
    scene: THREE.Scene | null
    camera: THREE.PerspectiveCamera | null
    renderer: THREE.WebGLRenderer | null
    particles: THREE.Points | null
    count: number
    animationId: number | null
  }>({
    scene: null,
    camera: null,
    renderer: null,
    particles: null,
    count: 0,
    animationId: null
  })

  useEffect(() => {
    if (!containerRef.current) return

    // 粒子配置
    const SEPARATION = 100
    const AMOUNTX = 50
    const AMOUNTY = 50

    // 初始化 Three.js
    const init = () => {
      if (!containerRef.current) return

      // 使用窗口尺寸（因为使用 fixed 定位）
      const width = window.innerWidth
      const height = window.innerHeight

      // 1. 场景与相机
      const newScene = new THREE.Scene()
      const newCamera = new THREE.PerspectiveCamera(
        75,
        width / height,
        1,
        10000
      )
      newCamera.position.z = 1000
      newCamera.position.y = 500

      // 2. 创建粒子几何体
      const numParticles = AMOUNTX * AMOUNTY
      const positions = new Float32Array(numParticles * 3)
      const scales = new Float32Array(numParticles)

      let i = 0
      let j = 0
      for (let ix = 0; ix < AMOUNTX; ix++) {
        for (let iy = 0; iy < AMOUNTY; iy++) {
          positions[i] = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2 // x
          positions[i + 1] = 0 // y
          positions[i + 2] = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2 // z
          scales[j] = 1
          i += 3
          j++
        }
      }

      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geometry.setAttribute('scale', new THREE.BufferAttribute(scales, 1))

      // 3. 材质
      const material = new THREE.PointsMaterial({
        color: 0xd4a574, // 棕黄色，与淡黄色背景搭配
        size: 5,
        transparent: true,
        opacity: 0.6,
        blending: THREE.NormalBlending
      })

      // 4. 生成粒子系统
      const newParticles = new THREE.Points(geometry, material)
      newScene.add(newParticles)

      // 5. 渲染器
      const newRenderer = new THREE.WebGLRenderer({ antialias: true })
      newRenderer.setPixelRatio(window.devicePixelRatio)
      newRenderer.setSize(width, height)
      newRenderer.setClearColor(0xfffbe6) // 设置淡黄色背景
      newRenderer.domElement.style.display = 'block'
      containerRef.current.appendChild(newRenderer.domElement)

      // 保存引用
      sceneRef.current.scene = newScene
      sceneRef.current.camera = newCamera
      sceneRef.current.renderer = newRenderer
      sceneRef.current.particles = newParticles
    }

    // 窗口缩放响应
    const onWindowResize = () => {
      if (sceneRef.current.camera && sceneRef.current.renderer) {
        const width = window.innerWidth
        const height = window.innerHeight
        sceneRef.current.camera.aspect = width / height
        sceneRef.current.camera.updateProjectionMatrix()
        sceneRef.current.renderer.setSize(width, height)
      }
    }

    // 渲染函数
    const render = () => {
      if (!sceneRef.current.particles || !sceneRef.current.camera || !sceneRef.current.renderer || !sceneRef.current.scene) {
        return
      }

      // 动态更新粒子高度和缩放
      const positions = sceneRef.current.particles.geometry.attributes.position.array as Float32Array
      let i = 0

      for (let ix = 0; ix < AMOUNTX; ix++) {
        for (let iy = 0; iy < AMOUNTY; iy++) {
          // 使用正弦和余弦函数创造"波浪"感
          positions[i + 1] =
            Math.sin((ix + sceneRef.current.count) * 0.3) * 50 +
            Math.sin((iy + sceneRef.current.count) * 0.5) * 50
          i += 3
        }
      }

      sceneRef.current.particles.geometry.attributes.position.needsUpdate = true

      // 相机缓慢旋转增加空间感
      if (sceneRef.current.camera) {
        const width = window.innerWidth
        sceneRef.current.camera.position.x +=
          (width / 4 - sceneRef.current.camera.position.x) * 0.01
        sceneRef.current.camera.lookAt(sceneRef.current.scene.position)
      }

      sceneRef.current.renderer.render(sceneRef.current.scene, sceneRef.current.camera)
      sceneRef.current.count += 0.1 // 运动速度
    }

    // 动画循环
    const animate = () => {
      sceneRef.current.animationId = requestAnimationFrame(animate)
      render()
    }

    // 延迟初始化，确保容器已渲染
    const initTimeout = setTimeout(() => {
      init()
      animate()
    }, 100)

    // 监听窗口大小变化
    window.addEventListener('resize', onWindowResize)

    // 监听容器大小变化
    let resizeObserver: ResizeObserver | null = null
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        onWindowResize()
      })
      resizeObserver.observe(containerRef.current)
    }

    // 清理函数
    return () => {
      clearTimeout(initTimeout)
      window.removeEventListener('resize', onWindowResize)
      if (resizeObserver && containerRef.current) {
        resizeObserver.unobserve(containerRef.current)
        resizeObserver.disconnect()
      }
      
      if (sceneRef.current.animationId !== null) {
        cancelAnimationFrame(sceneRef.current.animationId)
      }

      if (sceneRef.current.renderer) {
        containerRef.current?.removeChild(sceneRef.current.renderer.domElement)
        sceneRef.current.renderer.dispose()
      }

      if (sceneRef.current.particles) {
        sceneRef.current.particles.geometry.dispose()
        if (sceneRef.current.particles.material instanceof THREE.Material) {
          sceneRef.current.particles.material.dispose()
        }
      }

      sceneRef.current.scene = null
      sceneRef.current.camera = null
      sceneRef.current.renderer = null
      sceneRef.current.particles = null
      sceneRef.current.animationId = null
    }
  }, [])

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#fffbe6',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: -1
      }}
    />
  )
}
